import { useState } from "react";

// components
import SimpleCsvUploader from "components/molecules/SimpleCsvUploader";
import CreateBuildingsModal from "components/molecules/CreateBuildingsModal/CreateBuildingsModal";

// hooks
import posthog from "posthog-js";

const buildingArchetypeMapping: Record<string, string> = {
    "Enclosed mall": "enclosed_mall",
    "Full Service Restaurant": "full_service_restaurant",
    "Grocery Store": "food_retail_grocery",
    Hotel: "hotel",
    "Longterm Care": "longterm_care",
    "Medical Office": "medical_office",
    "Multi-Unit Residential": "multi_unit_residential",
    Office: "office",
    "Quick Service Restaurant": "quick_service_restaurant",
    "Retail Store": "non_food_retail_closed",
    School: "school",
    Townhomes: "townhomes",
    "Recreation Complex": "recreation_complex",
    Warehouse: "warehouse",
};

const energyTypeMapping: Record<string, string> = {
    Yes: "mixed",
    No: "electric",
    "I don't know": "mixed_inferred",
};

const headerMapping: Record<string, string> = {
    "Street address": "street_address",
    City: "city",
    "State/Province": "state_province",
    "Postal code": "postal_zip_code",
    Country: "country",
    Latitude: "latitude",
    Longitude: "longitude",
    "Building name": "building_name",
    "Building type": "building_archetype",
    "Is this building serviced by natural gas?": "energy_type",
    "Number of floors": "floors_above_grade",
    "Gross floor area ft²": "gross_floor_area",
    "Building Height ft": "building_height",
    "Year built": "year_built_original",
    "Available roof area ratio": "available_roof_area_ratio",
    "LED installed ratio": "led_installed_ratio",
    Tags: "tags",
    "Property name": "property_name",
    "Customer building identifier": "customer_building_identifier",
    Notes: "notes",
    "Leasing structure": "leasing_structure",
    "Assumed heating energy type": "assumed_heating_energy_type",
    "Assumed cooling energy type": "assumed_cooling_energy_type",
    "Assumed DHW energy type": "assumed_dhw_energy_type",
    "Assumed gross asset value": "assumed_gross_asset_value",
    "Assumed exit cap rate": "assumed_exit_cap_rate",
    "Assumed exit year": "assumed_exit_year",
    "Default discount rate": "default_discount_rate",
    "Grid carbon intensity schedule scenario": "default_grid_intensity_schedule_scenario",
    "Emission savings weight": "default_weight_emission_savings",
    "Net present value weight": "default_weight_net_present_value",
    "Default utility inflation rate": "default_utility_inflation_rate",
    "Default electricity utility rate": "default_electricity_utility_rate",
    "Default natural gas utility rate": "default_natural_gas_utility_rate",
    "Default steam utility rate": "default_steam_utility_rate",
    "Default landlord share of cost": "default_landlord_share_cost",
    "Default landlord share of electricity": "default_landlord_share_electricity",
    "Default landlord share of natural gas": "default_landlord_share_natural_gas",
    "Default landlord share of steam": "default_landlord_share_steam",
    "Cooling multiplier": "cooling_multiplier",
    "Heating multiplier": "heating_multiplier",
    "Lighting multiplier": "lighting_multiplier",
    "Plug load multiplier": "plug_load_multiplier",
    "Process multiplier": "process_multiplier",
    "Domestic hot water multiplier": "domestic_hot_water_multiplier",
    "Ventilation rate multiplier": "ventilation_rate_multiplier",
};

function BuildingsCsvUploader() {
    const [createBuildingsModalIsOpen, setCreateBuildingsModalIsOpen] = useState(false);
    const [buildingModelsInput, setBuildingModelsInput] = useState();

    const handleSubmitCreateBuildingsCsv = (response: any) => {
        const normalizedRows = response.map((row: any) => {
            const normalized: Record<string, any> = {};
            Object.entries(row).forEach(([key, value]) => {
                const fieldName = headerMapping[key] || key;
                let fieldValue = value;

                if (fieldName === "energy_type" && value) {
                    fieldValue = energyTypeMapping[value as string] || value;
                } else if (fieldName === "building_archetype" && value) {
                    fieldValue = buildingArchetypeMapping[value as string] || value;
                }

                normalized[fieldName] = fieldValue;
            });
            return normalized;
        });
        const bmInput = buildBuildingModelsInput(normalizedRows);
        setBuildingModelsInput(bmInput);
        setCreateBuildingsModalIsOpen(true);
        posthog.capture("Buildings CSV submitted");
    };

    return (
        <>
            <SimpleCsvUploader
                onResults={handleSubmitCreateBuildingsCsv}
                importIdentifier="buildings"
                importButtonStyle="secondary"
            />
            <CreateBuildingsModal
                modalIsOpen={createBuildingsModalIsOpen}
                buildingModelsInput={buildingModelsInput}
                setModalIsOpen={setCreateBuildingsModalIsOpen}
            />
        </>
    );
}

const buildBuildingModelsInput = (response: any) =>
    response.map((row: any) => {
        const toNumOrNull = (v: any) => (v === "" || v === undefined ? null : Number(v));
        const toStrOrNull = (v: any) => (v === "" || v === undefined ? null : v);

        const input: Record<string, any> = {
            street_address: row.street_address,
            city: row.city,
            state_province: row.state_province,
            postal_zip_code: row.postal_zip_code,
            country: row.country,
            building_archetype: row.building_archetype,
            floors_above_grade: toNumOrNull(row.floors_above_grade),
            gross_floor_area: toNumOrNull(row.gross_floor_area),
            latitude: toNumOrNull(row.latitude),
            longitude: toNumOrNull(row.longitude),
            building_name: toStrOrNull(row.building_name),
            energy_type: toStrOrNull(row.energy_type),
            building_height: toNumOrNull(row.building_height),
            year_built_original: toNumOrNull(row.year_built_original),
            available_roof_area_ratio: toNumOrNull(row.available_roof_area_ratio),
            led_installed_ratio: toNumOrNull(row.led_installed_ratio),
            tags: row.tags ? row.tags.split(",").map((t: string) => t.trim()) : null,
            property_name: toStrOrNull(row.property_name),
            customer_building_identifier: toStrOrNull(row.customer_building_identifier),
            notes: toStrOrNull(row.notes),
            leasing_structure: toStrOrNull(row.leasing_structure),
            assumed_heating_energy_type: toStrOrNull(row.assumed_heating_energy_type),
            assumed_cooling_energy_type: toStrOrNull(row.assumed_cooling_energy_type),
            assumed_dhw_energy_type: toStrOrNull(row.assumed_dhw_energy_type),
            assumed_gross_asset_value: toNumOrNull(row.assumed_gross_asset_value),
            assumed_exit_cap_rate: toNumOrNull(row.assumed_exit_cap_rate),
            assumed_exit_year: toNumOrNull(row.assumed_exit_year),
            default_discount_rate: toNumOrNull(row.default_discount_rate),
            default_grid_intensity_schedule_scenario: toStrOrNull(row.default_grid_intensity_schedule_scenario),
            default_weight_emission_savings: toNumOrNull(row.default_weight_emission_savings),
            default_weight_net_present_value: toNumOrNull(row.default_weight_net_present_value),
            default_utility_inflation_rate: toNumOrNull(row.default_utility_inflation_rate),
            default_electricity_utility_rate: toNumOrNull(row.default_electricity_utility_rate),
            default_natural_gas_utility_rate: toNumOrNull(row.default_natural_gas_utility_rate),
            default_steam_utility_rate: toNumOrNull(row.default_steam_utility_rate),
            default_landlord_share_cost: toNumOrNull(row.default_landlord_share_cost),
            default_landlord_share_electricity: toNumOrNull(row.default_landlord_share_electricity),
            default_landlord_share_natural_gas: toNumOrNull(row.default_landlord_share_natural_gas),
            default_landlord_share_steam: toNumOrNull(row.default_landlord_share_steam),
            cooling_multiplier: toNumOrNull(row.cooling_multiplier),
            heating_multiplier: toNumOrNull(row.heating_multiplier),
            lighting_multiplier: toNumOrNull(row.lighting_multiplier),
            plug_load_multiplier: toNumOrNull(row.plug_load_multiplier),
            process_multiplier: toNumOrNull(row.process_multiplier),
            domestic_hot_water_multiplier: toNumOrNull(row.domestic_hot_water_multiplier),
            ventilation_rate_multiplier: toNumOrNull(row.ventilation_rate_multiplier),
        };

        return input;
    });

export default BuildingsCsvUploader;
